import type { Point } from '@racemap/utilities/types/types';
import sax from 'sax';

// Intermediate type used while building a point during SAX parsing.
// `time` starts as 0 and is replaced with a Date before the point is pushed to output.
type DraftPoint = Omit<Point, 'time'> & { time: Date | number };

function readFileAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      const result = event.target?.result;
      if (typeof result !== 'string') {
        reject(new Error('Unexpected file content.'));
        return;
      }
      resolve(result);
    };
    reader.onerror = () => {
      reject(reader.error);
    };
    reader.readAsText(file);
  });
}

export class TrackPointsImport {
  SUPPORTED_EXTENSIONS = ['.gpx', '.kml', '.tcx'];

  async importFile(file: File): Promise<Array<Point>> {
    const [ext] = file.name.match(/\.[^.]+$/) || [''];
    const fileText = await readFileAsText(file);
    switch (ext.toLowerCase()) {
      case '.gpx':
        return this.parseGPX(fileText);
      case '.tcx':
        return this.parseTCX(fileText);
      case '.kml':
        return this.parseKML(fileText);
      default:
        return Promise.reject(new Error('Unknown file extension.'));
    }
  }

  parseGPX(fileText: string): Promise<Array<Point>> {
    return new Promise((resolve, reject) => {
      const tagStack: Array<string> = [];
      const output: Array<Point> = [];

      let _point: DraftPoint | null = null;
      let _text = '';

      const saxStream = sax.createStream(false, { lowercase: true });

      saxStream.on('opentag', ({ name, attributes }) => {
        _text = '';
        tagStack.push(name);
        switch (name) {
          case 'trkpt':
          case 'rtept':
            _point = {
              lat: Number.parseFloat(String(attributes.lat)),
              lng: Number.parseFloat(String(attributes.lon)),
              time: 0,
            };
            break;
        }
      });
      saxStream.on('closetag', (name) => {
        tagStack.pop();
        const parent = tagStack[tagStack.length - 1];
        switch (name) {
          case 'time':
            if (_point != null && (parent === 'trkpt' || parent === 'rtept')) {
              _point.time = new Date(_text);
            }
            break;
          case 'ele':
            if (_point != null && (parent === 'trkpt' || parent === 'rtept')) {
              _point.elv = Number.parseFloat(_text);
            }
            break;
          case 'trkpt':
          case 'rtept':
            if (_point != null) {
              output.push(_point as Point);
              _point = null;
            }
            break;
        }
      });
      saxStream.on('text', (text: string) => {
        _text += text;
      });
      saxStream.on('error', reject);
      saxStream.on('end', () => {
        resolve(output);
      });
      saxStream.end(fileText);
    });
  }

  parseTCX(fileText: string): Promise<Array<Point>> {
    return new Promise((resolve, reject) => {
      const tagStack: Array<string> = [];
      const output: Array<Point> = [];

      let _point: DraftPoint | null = null;
      let _text = '';

      const saxStream = sax.createStream(false, { lowercase: true });

      saxStream.on('opentag', ({ name }) => {
        _text = '';
        tagStack.push(name);
        switch (name) {
          case 'trackpoint':
            _point = { lat: 0, lng: 0, time: 0 };
            break;
        }
      });
      saxStream.on('closetag', (name) => {
        tagStack.pop();
        const parent = tagStack[tagStack.length - 1];
        const grandparent = tagStack[tagStack.length - 2];
        switch (name) {
          case 'time':
            if (_point != null && parent === 'trackpoint') {
              _point.time = new Date(_text);
            }
            break;
          case 'latitudedegrees':
            if (_point != null && parent === 'position' && grandparent === 'trackpoint') {
              _point.lat = Number.parseFloat(_text);
            }
            break;
          case 'longitudedegrees':
            if (_point != null && parent === 'position' && grandparent === 'trackpoint') {
              _point.lng = Number.parseFloat(_text);
            }
            break;
          case 'altitudemeters':
            if (_point != null && parent === 'trackpoint') {
              _point.elv = Number.parseFloat(_text);
            }
            break;
          case 'trackpoint':
            if (_point != null) {
              output.push(_point as Point);
              _point = null;
            }
            break;
        }
      });
      saxStream.on('text', (text: string) => {
        _text += text;
      });
      saxStream.on('error', reject);
      saxStream.on('end', () => {
        resolve(output);
      });
      saxStream.end(fileText);
    });
  }

  parseKML(fileText: string): Promise<Array<Point>> {
    return new Promise((resolve, reject) => {
      const tagStack: Array<string> = [];
      const output: Array<Point> = [];

      let _point: DraftPoint | null = null;
      let _time = 0;
      let _text = '';

      const saxStream = sax.createStream(false, { lowercase: true });

      saxStream.on('opentag', ({ name }) => {
        _text = '';
        tagStack.push(name);
        switch (name) {
          case 'placemark':
            _point = null;
            _time = 0;
            break;

          case 'point':
            _point = { lat: 0, lng: 0, time: 0 };
            break;
        }
      });
      saxStream.on('closetag', (name) => {
        tagStack.pop();
        switch (name) {
          case 'coordinates':
          case 'gx:coord':
            if (_point != null && tagStack[tagStack.length - 1] === 'point') {
              const [lng, lat, elv] = _text.trim().split(',');
              _point.lng = Number.parseFloat(lng);
              _point.lat = Number.parseFloat(lat);
              if (elv != null) {
                _point.elv = Number.parseFloat(elv);
              }
            }
            break;

          case 'begin':
            if (tagStack[tagStack.length - 1] === 'timespan') {
              _time = Date.parse(_text);
            }
            break;

          case 'placemark':
            if (_point != null && _time > 0) {
              _point.time = new Date(_time);
              output.push(_point as Point);
              _point = null;
            }
            break;
        }
      });
      saxStream.on('text', (text) => {
        _text += text;
      });
      saxStream.on('error', reject);
      saxStream.on('end', () => {
        resolve(output);
      });
      saxStream.end(fileText);
    });
  }
}
